import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { Check, Zap } from "lucide-react";
import { StarRating } from "@/components/laneguide/StarRating";
import { Header } from "@/components/laneguide/Header";
import { Footer } from "@/components/laneguide/Footer";
import { LenderLogo } from "@/components/laneguide/LenderLogo";
import { getResultsForForm } from "@/lib/lenderSelection";
import { applyUrl } from "@/lib/lenders";
import { FaqSection } from "@/components/laneguide/PageFaq";

export const Route = createFileRoute("/resultat")({
  head: () => ({
    meta: [
      { title: "Dina låneförslag | Svensk Låneguide" },
      { name: "description", content: "Personliga låneförslag baserat på ditt lånetest." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResultatPage,
});

function formatKr(n: number) {
  return new Intl.NumberFormat("sv-SE").format(n) + " kr";
}

function ResultatPage() {
  const form = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("laneguide-form");
      if (raw) return JSON.parse(raw);
    } catch {}
    return { amount: 50000, purpose: "ovrigt", remarks: "nej" };
  }, []);

  const lenders = useMemo(
    () =>
      getResultsForForm({
        amount: Number(form.amount) || 50000,
        purpose: String(form.purpose || "ovrigt"),
        hasRemarks: form.remarks === "ja",
        income: form.income ? Number(form.income) : undefined,
      }),
    [form],
  );

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-wider text-primary">Resultat</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Dina bästa <span className="underline-brand">låneförslag</span>
          </h1>
          <p className="mt-2 text-sm text-foreground/70">
            Baserat på {formatKr(Number(form.amount) || 50000)} · {form.purpose} · anmärkning: {form.remarks}
            {form.source === "lanetest" && form.income
              ? ` · månadsinkomst: ${formatKr(Number(form.income))}`
              : ""}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {lenders.map((o, idx) => (
            <article
              key={o.id}
              className={`flex flex-col gap-4 rounded-2xl border bg-card p-6 ${
                idx === 0 ? "border-primary/40 shadow-elevated ring-1 ring-primary/10" : "border-border shadow-card"
              }`}
            >
              <div className="flex items-center gap-3">
                <LenderLogo lender={o} size={56} />
                <div>
                  <h3 className="font-display text-lg font-extrabold text-foreground">{o.name}</h3>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <StarRating rating={o.rating} />
                    <span className="font-semibold text-foreground">{o.rating}/5</span>
                  </div>
                </div>
              </div>
              <dl className="space-y-2 border-t border-border pt-3 text-sm">
                <div className="flex justify-between"><dt className="text-muted-foreground">Belopp</dt><dd className="font-bold text-foreground">{formatKr(o.minAmount)} – {formatKr(o.maxAmount)}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Löptid</dt><dd className="font-bold text-foreground">{o.term}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Ränta</dt><dd className="font-display text-lg font-extrabold text-primary">{o.rate}</dd></div>
              </dl>
              <div className="flex flex-wrap gap-1.5">
                {(o.tags ?? []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-md bg-primary-soft px-2.5 py-1 text-[11px] font-semibold text-primary">
                    <Check className="h-3 w-3" /> {t}
                  </span>
                ))}
              </div>
              <a href={applyUrl(o, form.source === "lanetest" ? "lanetest" : "resultat", "result-card")} target="_blank" rel="noopener noreferrer sponsored" className="mt-auto flex items-center justify-center gap-1.5 rounded-xl bg-cta px-5 py-3.5 text-sm font-bold text-cta-foreground transition-all hover:brightness-105">
                <Zap className="h-4 w-4" /> Ansök nu
              </a>
            </article>
          ))}
          {lenders.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
              Inga långivare matchar dina val. <a href="/lanetest" className="font-semibold text-primary underline">Gör om lånetestet</a>.
            </div>
          )}
        </div>
        <FaqSection />
      </main>
      <Footer />
    </div>
  );
}
